import { useFetchGtmArchitectures } from '../../hooks/gtm-solution-hooks';
import { useFetchCiscoProducts } from '../../hooks/product-hooks';
import { chartOptions } from '../../support/customTileTransformUtils';
import { renderChips } from '../Common/RenderChips';
import { safeRefRender } from './Safe/SafeRefName';

/**
 * Product lookups for the SAFE mappings.
 *
 * `vendor__is_cisco` matches the `products` model above: a SAFE capability is mapped to the Cisco
 * product that delivers it, and third-party coverage is recorded on the assessment rather than
 * here. `size` overrides the 1000-row default in `fetchItems`, which the Cisco product list is
 * close enough to that a silent truncation would show real mappings as `#<id>`.
 */
const SAFE_PRODUCT_OPTION_PARAMS = { vendor__is_cisco: true, size: 5000 };

export type TextFieldConfig = {
  key: string;
  label: string;
  type: 'text';
  required?: boolean;
  multiLine?: boolean;
};

export type StaticOption = { value: string | number; label: string };

export type SelectFieldConfig = {
  key: string;
  label: string;
  type: 'select';
  required?: boolean;
  // A select is backed EITHER by a DB endpoint (optionsEndpoint, options resolved to rows) OR by
  // a fixed `options` list for enum-style choices (e.g. severity). One of the two must be set.
  optionsEndpoint?: string;
  optionsParams?: Record<string, any>;
  optionLabel?: string;
  options?: StaticOption[];
};

export type DateTimeFieldConfig = {
  key: string;
  label: string;
  type: 'datetime';
  required?: boolean;
};

export type BooleanFieldConfig = {
  key: string;
  label: string;
  type: 'boolean';
  required?: boolean;
};

export type DateFieldConfig = {
  key: string;
  label: string;
  type: 'date';
  required?: boolean;
};

export type MultiSelectFieldConfig = {
  key: string;
  label: string;
  type: 'multiselect';
  required?: boolean;
  optionsEndpoint: string;
  optionsParams?: Record<string, any>;
  optionLabel: string;
};

export type ModelField =
  | TextFieldConfig
  | SelectFieldConfig
  | DateTimeFieldConfig
  | BooleanFieldConfig
  | DateFieldConfig
  | MultiSelectFieldConfig;

export type ModelColumnConfig = {
  key: string;
  label: string;
  render?: (row: any) => React.ReactNode;
  minWidth?: number;
  maxWidth?: number;
  truncate?: boolean;
  truncateAt?: number;
  tooltipOnTruncate?: boolean;
  headerNoWrap?: boolean;
};

export type ModelConfig = {
  key: string;
  label: string;

  endpoint?:
    | string
    | {
    url: string;
    params?: Record<string, any>;
  };
  fetchHook?: () => { data: any; isLoading: boolean; error: any };

  columns: ModelColumnConfig[];
  fields: ModelField[];

  perms?: {
    pageView?: string;
    read?: string;
    create?: string;
    update?: string;
    delete?: string;
  };

  scopeKey?: 'products' | 'goToMarkets';
};

export function isSelectField(field: ModelField): field is SelectFieldConfig {
  return field.type === 'select';
}

export function isMultiSelectField(field: ModelField): field is MultiSelectFieldConfig {
  return field.type === 'multiselect';
}

export function normalizeEndpoint(
  endpoint: ModelConfig['endpoint']
): { url: string; params?: Record<string, any> } {
  if (typeof endpoint === 'string') return { url: endpoint };
  return endpoint;
}

export const MODELS: { [key: string]: ModelConfig } = {
  users: {
    key: 'users',
    label: 'Users',
    endpoint: 'db/user',
    perms: {
      pageView: 'page.usersAdmin.view',
      read: 'api.user.read',
      create: 'api.user.create',
      update: 'api.user.update',
      delete: 'api.user.delete',
    },
    columns: [
      { key: 'email', label: 'Email' },
      { key: 'sales_level_1', label: 'Sales Level 1', render: renderChips('sales_level_1') },
      { key: 'sales_level_2', label: 'Sales Level 2', render: renderChips('sales_level_2') },
      { key: 'sales_level_3', label: 'Sales Level 3', render: renderChips('sales_level_3') },
      { key: 'sales_level_4', label: 'Sales Level 4', render: renderChips('sales_level_4') },
      { key: 'manager', label: 'Manager' },
    ],
    fields: [
      { key: 'email', label: 'Email', type: 'text', required: true },
      {
        key: 'manager',
        label: 'Manager',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/user',
        optionLabel: 'email',
      },
      { key: 'sales_level_1', label: 'Sales Level 1', type: 'text', required: true },
      { key: 'sales_level_2', label: 'Sales Level 2', type: 'text', required: false },
      { key: 'sales_level_3', label: 'Sales Level 3', type: 'text', required: false },
      { key: 'sales_level_4', label: 'Sales Level 4', type: 'text', required: false },
      { key: 'sales_level_5', label: 'Sales Level 5', type: 'text', required: false },
      { key: 'sales_level_6', label: 'Sales Level 6', type: 'text', required: false },
      { key: 'registry', label: 'Registry', type: 'text', required: false },
      { key: 'is_manager', label: 'Is Manager', type: 'boolean', required: false },
    ],
  },

  roles: {
    key: 'roles',
    label: 'Roles',
    endpoint: 'db/role',
    perms: {
      pageView: 'page.rolesAdmin.view',
      read: 'api.role.read',
      create: 'api.role.create',
      update: 'api.role.update',
      delete: 'api.role.delete',
    },
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description' },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text' },
    ],
  },


  userGroups: {
    key: 'userGroups',
    label: 'User Groups',
    endpoint: 'db/user-group',
    perms: {
      pageView: 'page.userGroupsAdmin.view',
      read: 'api.user-group.read',
      create: 'api.user-group.create',
      update: 'api.user-group.update',
      delete: 'api.user-group.delete',
    },
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description' },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text' },
    ],
  },

  calendarEvents: {
    key: 'calendarEvents',
    label: 'Events',
    endpoint: 'db/calendar-event',
    perms: {
      // no `read`: General User has it, and canSeeTab would show this tab to everyone
      pageView: 'page.eventsAdmin.view',
      create: 'api.calendar-event.create',
      update: 'api.calendar-event.update',
      delete: 'api.calendar-event.delete',
    },
    columns: [
      { key: 'title', label: 'Title', minWidth: 220 },
      { key: 'start_date', label: 'Start' },
      { key: 'end_date', label: 'End' },
      { key: 'event_type', label: 'Type' },
      { key: 'region', label: 'Region' },
      { key: 'location', label: 'Location' },
      {
        key: 'active',
        label: 'Visible',
        render: (row: any) => (row.active ? 'Yes' : 'Hidden'),
      },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
      { key: 'start_date', label: 'Start date', type: 'date', required: true },
      { key: 'end_date', label: 'End date (optional)', type: 'date' },
      {
        key: 'event_type',
        label: 'Type',
        type: 'select',
        required: true,
        options: ['Conference', 'Hybrid', 'In-Person', 'Virtual', 'Webinar', 'Workshop'].map((v) => ({
          value: v,
          label: v,
        })),
      },
      {
        key: 'region',
        label: 'Region',
        type: 'select',
        required: true,
        options: [
          { value: 'global', label: 'Global (everyone)' },
          { value: 'amer', label: 'AMER' },
          { value: 'emea', label: 'EMEA' },
          { value: 'apjc', label: 'APJC' },
        ],
      },
      { key: 'location', label: 'Location', type: 'text' },
      { key: 'link', label: 'Link (URL or /internal-path)', type: 'text' },
      { key: 'required', label: 'Mandatory', type: 'boolean' },
      { key: 'active', label: 'Visible on calendar', type: 'boolean' },
    ],
  },

  banners: {
    key: 'banners',
    label: 'Banners',
    endpoint: 'db/system-banner',
    // NOTE: `read` is intentionally omitted. `api.system-banner.read` is granted to every
    // authenticated user (General User) so banners render on the landing page — including it here
    // would make `canSeeTab`'s read fallback show this admin tab to everyone. The tab is gated on
    // `page.bannersAdmin.view` (admins), and admin.full satisfies read for the table fetch.
    perms: {
      pageView: 'page.bannersAdmin.view',
      create: 'api.system-banner.create',
      update: 'api.system-banner.update',
      delete: 'api.system-banner.delete',
    },
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'title', label: 'Title' },
      { key: 'severity', label: 'Severity' },
      { key: 'active', label: 'Active', render: (row: any) => (row.active ? 'Yes' : 'No') },
      { key: 'order', label: 'Order' },
      {
        key: 'last_updated',
        label: 'Last Updated',
        minWidth: 190,
        maxWidth: 210,
        truncate: true,
        render: (row: any) => {
          const date = new Date(row.last_updated);
          return isNaN(date.getTime()) ? '—' : date.toLocaleString();
        },
      },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'body', label: 'Body', type: 'text', multiLine: true },
      { key: 'cta_label', label: 'Button Label', type: 'text' },
      { key: 'cta_url', label: 'Button Link (path or URL)', type: 'text' },
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        required: true,
        options: [
          { value: 'info', label: 'Info' },
          { value: 'promo', label: 'Promo' },
          { value: 'warning', label: 'Warning' },
        ],
      },
      { key: 'active', label: 'Active', type: 'boolean' },
      { key: 'starts_at', label: 'Starts At', type: 'datetime' },
      { key: 'ends_at', label: 'Ends At', type: 'datetime' },
      { key: 'order', label: 'Order', type: 'text' },
    ],
  },

  customTiles: {
    key: 'customTiles',
    label: 'Custom Tiles',
    endpoint: 'db/custom-tile',
    perms: {
      pageView: 'page.customTilesAdmin.view',
      read: 'api.custom-tile.read',
      create: 'api.custom-tile.create',
      update: 'api.custom-tile.update',
      delete: 'api.custom-tile.delete',
    },
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Tile Name' },
      {
        key: 'chart_type',
        label: 'Chart Type',
        render: (row: any) => {
          const chartOption = chartOptions.find((option) => option.value === row.chart_type);
          return chartOption ? chartOption.label : 'Unknown Chart Type';
        },
      },
      {
        key: 'last_updated',
        label: 'Last Updated',
        minWidth: 190,
        maxWidth: 210,
        truncate: true,
        render: (row: any) => {
          const date = new Date(row.last_updated);
          return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
        },
      },
    ],
    fields: [
      { key: 'id', label: 'Tile ID', type: 'text', required: true },
      { key: 'name', label: 'Tile Name', type: 'text', required: true },
      { key: 'chart_type', label: 'Chart Type', type: 'text', required: true },
      { key: 'queries', label: 'Queries', type: 'text', required: false },
      { key: 'transformations', label: 'Transformations', type: 'text', required: false },
      { key: 'settings', label: 'Settings', type: 'text', required: false },
    ],
  },

  azureADGroups: {
    key: 'azureADGroups',
    label: 'Azure AD Groups',
    endpoint: 'db/azure-ad-group',
    perms: {
      pageView: 'page.azureAdGroupsAdmin.view',
      read: 'api.azure-ad-group.read',
      create: 'api.azure-ad-group.create',
      update: 'api.azure-ad-group.update',
      delete: 'api.azure-ad-group.delete',
    },
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description' },
      { key: 'group_id', label: 'Group ID' },
    ],
    fields: [
      { key: 'id', label: 'Group ID', type: 'text', required: true },
      { key: 'displayName', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text' },
    ],
  },

  products: {
    key: 'products',
    label: 'Products',
    endpoint: {
      url: 'db/product',
      params: {
        vendor__is_cisco: true,
      },
    },
    fetchHook: useFetchCiscoProducts,
    perms: {
      pageView: 'page.productEditor.view',
      read: 'api.product.read',
      create: 'api.product.create',
      update: 'api.product.update',
      delete: 'api.product.delete',
    },
    scopeKey: 'products',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'id', label: 'Product ID' },
      {
        key: 'description',
        label: 'Description',
        minWidth: 190,
        maxWidth: 700,
        truncate: true,
      },
      { key: 'vendor', label: 'Vendor', render: (row: any) => row.vendor?.name || '' },
      {
        key: 'last_updated',
        label: 'Last Updated',
        minWidth: 190,
        maxWidth: 210,
        truncate: true,
        render: (row: any) => {
          const date = new Date(row.last_updated);
          return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
        },
      },
    ],
    fields: [],
  },

  goToMarkets: {
    key: 'goToMarkets',
    label: 'Go-To-Market',
    endpoint: {
      url: 'db/go-to-market',
      params: {
        type: 'architecture',
      },
    },
    fetchHook: useFetchGtmArchitectures,
    perms: {
      pageView: 'page.gtmEditor.view',
      read: 'api.go-to-market.read',
      create: 'api.go-to-market.create',
      update: 'api.go-to-market.update',
      delete: 'api.go-to-market.delete',
    },
    scopeKey: 'goToMarkets',
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
    ],
    fields: [],
  },

  escalationTypes: {
    key: 'escalationTypes',
    label: 'Escalation Types',
    endpoint: 'db/escalation-type',
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 220 },
      { key: 'description', label: 'Description', minWidth: 300, truncateAt: 80 },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
    ],
  },

  escalationAssignmentTypes: {
    key: 'escalationAssignmentTypes',
    label: 'Escalation Assignment Types',
    endpoint: 'db/escalation-assignment-type',
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 220 },
      { key: 'level', label: 'Level', minWidth: 100 },
      { key: 'description', label: 'Description', minWidth: 320, truncateAt: 80 },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'level', label: 'Level', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
    ],
  },

  escalationProductSupport: {
    key: 'escalationProductSupport',
    label: 'Product Escalation Routing',
    endpoint: {
      url: 'db/escalation-product-support',
      params: { detailed: true, depth: 2 },
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'product_name', label: 'Product', minWidth: 260 },
      { key: 'assignment_type_name', label: 'Routing Option', minWidth: 180 },
      {
        key: 'team_name',
        label: 'Team Name',
        minWidth: 220,
        render: (row: any) => row.group_source === 'external' ? '' : row.team_name || '',
      },
      {
        key: 'team_email',
        label: 'Team Email',
        minWidth: 220,
        truncate: true,
        render: (row: any) => row.group_source === 'external' ? '' : row.team_email || '',
      },
      {
        key: 'is_active',
        label: 'Active',
        minWidth: 100,
        render: (row: { is_active?: boolean }) => (row.is_active ? 'Yes' : 'No'),
      },
      { key: 'updated_by', label: 'Updated By', minWidth: 180 },
    ],
    fields: [],
  },

  escalationBuRequestMappings: {
    key: 'escalationBuRequestMappings',
    label: 'BU Request Mappings',
    endpoint: {
      url: 'db/escalation-bu-request-mapping',
      params: { detailed: true, depth: 2 },
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },

      {
        key: 'product',
        label: 'Product',
        minWidth: 240,
        render: (row: any) => row.product_name || row.product?.name || row.product || '',
      },

      {
        key: 'assignment_type',
        label: 'Assignment Type',
        minWidth: 220,
        render: (row: any) =>
          row.assignment_type_name ||
          row.assignment_type?.name ||
          row.assignment_type ||
          '',
      },

      {
        key: 'escalation_type',
        label: 'Escalation Type',
        minWidth: 220,
        render: (row: any) =>
          row.escalation_type_name ||
          row.escalation_type?.name ||
          'Any escalation type',
      },

      {
        key: 'request_type_name',
        label: 'Jira Request Type',
        minWidth: 240,
        render: (row: any) => row.request_type_name || '',
      },

      {
        key: 'request_type_id',
        label: 'Request Type ID',
        minWidth: 150,
        render: (row: any) => row.request_type_id || '',
      },

      {
        key: 'project_key',
        label: 'Project Key',
        minWidth: 120,
        render: (row: any) => row.project_key || '',
      },

      {
        key: 'project_name',
        label: 'Project Name',
        minWidth: 220,
        render: (row: any) => row.project_name || '',
      },

      {
        key: 'service_desk_id',
        label: 'Service Desk ID',
        minWidth: 150,
        render: (row: any) => row.service_desk_id || '',
      },

      {
        key: 'priority',
        label: 'Priority',
        minWidth: 100,
        render: (row: any) => row.priority ?? '',
      },

      {
        key: 'is_active',
        label: 'Active',
        minWidth: 100,
        render: (row: { is_active?: boolean }) => (row.is_active ? 'Yes' : 'No'),
      },

      {
        key: 'is_allowed_by_current_policy',
        label: 'Policy Allowed',
        minWidth: 140,
        render: (row: any) => {
          if (row.is_allowed_by_current_policy === undefined) return '';
          return row.is_allowed_by_current_policy ? 'Yes' : 'No';
        },
      },

      {
        key: 'notes',
        label: 'Notes',
        minWidth: 280,
        truncateAt: 100,
      },

      {
        key: 'last_updated',
        label: 'Last Updated',
        minWidth: 180,
        truncate: true,
        render: (row: any) => {
          if (!row.last_updated) return '';
          const date = new Date(row.last_updated);
          return isNaN(date.getTime()) ? row.last_updated : date.toLocaleString();
        },
      },
    ],
    fields: [],
  },

  escalations: {
    key: 'escalations',
    label: 'Escalations',
    endpoint: {
      url: 'db/escalation',
      params: { detailed: true, depth: 2 },
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'product_name', label: 'Product', minWidth: 220 },
      {
        key: 'escalation_type_name',
        label: 'Escalation Type',
        minWidth: 180,
        render: (row: any) => row.escalation_type_name || row.escalation_type?.name || '',
      },
      {
        key: 'status',
        label: 'Status',
        minWidth: 140,
        render: (row: any) => row.status?.title || row.status_name || '',
      },
      {
        key: 'requested_by',
        label: 'Requested By',
        minWidth: 220,
        render: (row: any) => row.requested_by?.email || '',
      },
      { key: 'stage', label: 'Stage', minWidth: 100 },
      { key: 'priority', label: 'Priority', minWidth: 100 },
      { key: 'desired_completion_date', label: 'Desired Completion', minWidth: 160 },
      {
        key: 'created',
        label: 'Created',
        minWidth: 180,
        truncate: true,
        render: (row: any) => {
          const date = new Date(row.created);
          return isNaN(date.getTime()) ? row.created : date.toLocaleString();
        },
      },
    ],
    fields: [],
  },

  escalationApprovals: {
    key: 'escalationApprovals',
    label: 'Escalation Workflow Approvals',
    endpoint: {
      url: 'db/escalation-approval',
      params: { detailed: true, depth: 3 },
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      {
        key: 'escalation',
        label: 'Escalation ID',
        minWidth: 120,
        render: (row: any) => row.escalation?.id || '',
      },
      {
        key: 'approval_request',
        label: 'Approval Request ID',
        minWidth: 150,
        render: (row: any) => row.approval_request?.id || '',
      },
      { key: 'level', label: 'Level', minWidth: 100 },
      {
        key: 'approval_status',
        label: 'Approval Status',
        minWidth: 160,
        render: (row: any) =>
          row.approval_request?.status?.title || row.approval_request?.status_name || '',
      },
      {
        key: 'approver',
        label: 'Approver',
        minWidth: 220,
        render: (row: any) => row.approval_request?.approver?.email || '',
      },
      {
        key: 'target_assignment_type',
        label: 'Target Assignment Type',
        minWidth: 220,
        render: (row: any) => row.target_assignment_type?.name || '',
      },
      {
        key: 'assignment',
        label: 'Assignment ID',
        minWidth: 130,
        render: (row: any) => row.assignment?.id || '',
      },
      {
        key: 'created',
        label: 'Created',
        minWidth: 180,
        truncate: true,
        render: (row: any) => {
          const date = new Date(row.created);
          return isNaN(date.getTime()) ? row.created : date.toLocaleString();
        },
      },
    ],
    fields: [],
  },

  escalationHistory: {
    key: 'escalationHistory',
    label: 'Escalation History',
    endpoint: {
      url: 'db/escalation-history',
      params: { detailed: true, depth: 2 },
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      {
        key: 'escalation',
        label: 'Escalation ID',
        minWidth: 120,
        render: (row: any) => row.escalation?.id || '',
      },
      { key: 'event_type', label: 'Event Type', minWidth: 160 },
      { key: 'previous_value', label: 'Previous Value', minWidth: 160 },
      { key: 'new_value', label: 'New Value', minWidth: 160 },
      {
        key: 'performed_by',
        label: 'Performed By',
        minWidth: 220,
        render: (row: any) => row.performed_by?.email || '',
      },
      { key: 'notes', label: 'Notes', minWidth: 300, truncateAt: 100 },
      {
        key: 'created',
        label: 'Created',
        minWidth: 180,
        truncate: true,
        render: (row: any) => {
          const date = new Date(row.created);
          return isNaN(date.getTime()) ? row.created : date.toLocaleString();
        },
      },
    ],
    fields: [],
  },

  // ---------------------------------------------------------------------------------------
  // SAFE
  //
  // The reference tables and the six join tables behind the SAFE Architect and SAFE Risk
  // assessments, surfaced by `Admin/Safe/AdminSafe.tsx`.
  //
  // Every foreign key column renders through `safeRefRender`, because the serializers return
  // ids and these join tables are almost nothing but foreign keys. One react-query key per
  // reference endpoint means a 400-row join table costs one extra request, not 400.
  //
  // All of them share `pageView: 'page.safeAdmin.view'` and `api.<slug>.<action>`, seeded by
  // rbac migration `0003_seed_safe_permissions` and granted to the `SAFE Admin` role.
  // ---------------------------------------------------------------------------------------

  safeBusinessTechnologies: {
    key: 'safeBusinessTechnologies',
    label: 'Business Critical Technologies',
    endpoint: 'db/business-technology',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.business-technology.read',
      create: 'api.business-technology.create',
      update: 'api.business-technology.update',
      delete: 'api.business-technology.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 220 },
      { key: 'segment', label: 'Segment', minWidth: 160 },
      { key: 'description', label: 'Description', minWidth: 300, truncateAt: 100 },
      { key: 'adoption_rate', label: 'Adoption Rate', minWidth: 130 },
      { key: 'cost', label: 'Cost', minWidth: 100 },
      { key: 'updated_by', label: 'Updated By', minWidth: 180 },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
      { key: 'segment', label: 'Segment', type: 'text' },
      { key: 'impact', label: 'Impact', type: 'text', multiLine: true },
      { key: 'update', label: 'Update Notes', type: 'text', multiLine: true },
      { key: 'adoption_rate', label: 'Adoption Rate', type: 'text' },
      { key: 'cost', label: 'Cost', type: 'text' },
      // Threat-actor relevance, scored per business critical technology.
      { key: 'operations', label: 'Operations', type: 'text', multiLine: true },
      { key: 'assets', label: 'Assets', type: 'text', multiLine: true },
      { key: 'individuals', label: 'Individuals', type: 'text', multiLine: true },
      { key: 'thirdparty', label: 'Third Party', type: 'text', multiLine: true },
      { key: 'nation', label: 'Nation State', type: 'text', multiLine: true },
      { key: 'targets', label: 'Targets', type: 'text', multiLine: true },
      // Per-vertical relevance.
      { key: 'enterprise', label: 'Enterprise', type: 'text', multiLine: true },
      { key: 'commercial', label: 'Commercial', type: 'text', multiLine: true },
      { key: 'healthcare', label: 'Healthcare', type: 'text', multiLine: true },
      { key: 'manufacturing', label: 'Manufacturing', type: 'text', multiLine: true },
      { key: 'education', label: 'Education', type: 'text', multiLine: true },
      { key: 'financial', label: 'Financial', type: 'text', multiLine: true },
      { key: 'localgov', label: 'Local Government', type: 'text', multiLine: true },
      { key: 'federalgov', label: 'Federal Government', type: 'text', multiLine: true },
      { key: 'utilities', label: 'Utilities', type: 'text', multiLine: true },
    ],
  },

  safeRisks: {
    key: 'safeRisks',
    label: 'Risks',
    endpoint: 'db/risk',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.risk.read',
      create: 'api.risk.create',
      update: 'api.risk.update',
      delete: 'api.risk.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 260 },
      { key: 'impact', label: 'Impact', minWidth: 200, truncateAt: 80 },
      { key: 'risk_owner', label: 'Risk Owner', minWidth: 180 },
      { key: 'mitigation_plan', label: 'Mitigation Plan', minWidth: 300, truncateAt: 100 },
      { key: 'updated_by', label: 'Updated By', minWidth: 180 },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      // `risk.impact` is a TextField holding the impact *name*, not a foreign key. It stays a
      // text field on purpose: a select writes the option's id (see `AdminFormFields`), which
      // is exactly the ids-in-a-name-column state the framework_tool merge had to clean up.
      { key: 'impact', label: 'Impact', type: 'text', multiLine: true },
      { key: 'mitigation_plan', label: 'Mitigation Plan', type: 'text', multiLine: true },
      { key: 'risk_owner', label: 'Risk Owner', type: 'text' },
      { key: 'update', label: 'Update Notes', type: 'text', multiLine: true },
    ],
  },

  safeCapabilities: {
    key: 'safeCapabilities',
    label: 'Capabilities',
    endpoint: 'db/capability',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.capability.read',
      create: 'api.capability.create',
      update: 'api.capability.update',
      delete: 'api.capability.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 240 },
      { key: 'domain', label: 'Domain', minWidth: 160 },
      { key: 'maturity_level', label: 'Maturity', minWidth: 120 },
      { key: 'priority', label: 'Priority', minWidth: 100 },
      { key: 'status', label: 'Status', minWidth: 120 },
      {
        key: 'architecture',
        label: 'Architecture',
        minWidth: 180,
        render: safeRefRender('db/architecture', 'architecture'),
      },
      {
        key: 'ui_visible',
        label: 'Visible',
        minWidth: 100,
        render: (row: any) => (row.ui_visible ? 'Yes' : 'No'),
      },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
      {
        key: 'architecture',
        label: 'Architecture',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/architecture',
        optionLabel: 'name',
      },
      { key: 'domain', label: 'Domain', type: 'text' },
      { key: 'maturity_level', label: 'Maturity Level', type: 'text' },
      { key: 'priority', label: 'Priority', type: 'text' },
      { key: 'server', label: 'Server', type: 'text' },
      { key: 'image', label: 'Image', type: 'text' },
      { key: 'owner', label: 'Owner', type: 'text' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'centralized', label: 'Centralized', type: 'boolean' },
      { key: 'ui_visible', label: 'Visible In Assessments', type: 'boolean' },
    ],
  },

  safePins: {
    key: 'safePins',
    label: 'Places In The Network',
    endpoint: 'db/pin',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.pin.read',
      create: 'api.pin.create',
      update: 'api.pin.update',
      delete: 'api.pin.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 240 },
      { key: 'order', label: 'Order', minWidth: 100 },
      { key: 'image', label: 'Image', minWidth: 200 },
      { key: 'updated_by', label: 'Updated By', minWidth: 180 },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'image', label: 'Image', type: 'text' },
      // Drives the order the places appear in, in the filters and the flow diagram.
      { key: 'order', label: 'Order', type: 'text' },
      { key: 'update', label: 'Update Notes', type: 'text', multiLine: true },
    ],
  },

  safeSecurityFunctions: {
    key: 'safeSecurityFunctions',
    label: 'Security Functions',
    endpoint: 'db/security-function',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.security-function.read',
      create: 'api.security-function.create',
      update: 'api.security-function.update',
      delete: 'api.security-function.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 300 },
    ],
    fields: [{ key: 'name', label: 'Name', type: 'text', required: true }],
  },

  safeDomains: {
    key: 'safeDomains',
    label: 'Domains',
    endpoint: 'db/domain',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.domain.read',
      create: 'api.domain.create',
      update: 'api.domain.update',
      delete: 'api.domain.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 300 },
    ],
    fields: [{ key: 'name', label: 'Name', type: 'text', required: true }],
  },

  safeImpacts: {
    key: 'safeImpacts',
    label: 'Impacts',
    endpoint: 'db/impact',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.impact.read',
      create: 'api.impact.create',
      update: 'api.impact.update',
      delete: 'api.impact.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'name', label: 'Name', minWidth: 300 },
    ],
    fields: [{ key: 'name', label: 'Name', type: 'text', required: true }],
  },

  safeBusinessTechnologyToRisk: {
    key: 'safeBusinessTechnologyToRisk',
    label: 'Technology → Risk',
    endpoint: 'db/business-technology-to-risk',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.business-technology-to-risk.read',
      create: 'api.business-technology-to-risk.create',
      update: 'api.business-technology-to-risk.update',
      delete: 'api.business-technology-to-risk.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      {
        key: 'business_technology',
        label: 'Business Critical Technology',
        minWidth: 260,
        headerNoWrap: true,
        render: safeRefRender('db/business-technology', 'business_technology'),
      },
      {
        key: 'risk',
        label: 'Risk',
        minWidth: 260,
        render: safeRefRender('db/risk', 'risk'),
      },
      { key: 'severity', label: 'Severity', minWidth: 120 },
      { key: 'description', label: 'Description', minWidth: 300, truncateAt: 100 },
      { key: 'updated_by', label: 'Updated By', minWidth: 180 },
    ],
    fields: [
      {
        key: 'business_technology',
        label: 'Business Critical Technology',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/business-technology',
        optionLabel: 'name',
      },
      {
        key: 'risk',
        label: 'Risk',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/risk',
        optionLabel: 'name',
      },
      // Multiplies the technology's score into the risk score on the report; the assessment
      // reads it as one of High / Medium / Low.
      { key: 'severity', label: 'Severity (High / Medium / Low)', type: 'text' },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
    ],
  },

  safeRiskToCapability: {
    key: 'safeRiskToCapability',
    label: 'Risk → Capability',
    endpoint: 'db/risk-to-capability',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.risk-to-capability.read',
      create: 'api.risk-to-capability.create',
      update: 'api.risk-to-capability.update',
      delete: 'api.risk-to-capability.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      {
        key: 'risk',
        label: 'Risk',
        minWidth: 260,
        render: safeRefRender('db/risk', 'risk'),
      },
      {
        key: 'capability',
        label: 'Capability',
        minWidth: 260,
        render: safeRefRender('db/capability', 'capability'),
      },
      { key: 'description', label: 'Description', minWidth: 300, truncateAt: 100 },
      { key: 'updated_by', label: 'Updated By', minWidth: 180 },
    ],
    fields: [
      {
        key: 'risk',
        label: 'Risk',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/risk',
        optionLabel: 'name',
      },
      {
        key: 'capability',
        label: 'Capability',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/capability',
        optionLabel: 'name',
      },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
    ],
  },

  safeCapabilityToPin: {
    key: 'safeCapabilityToPin',
    label: 'Capability → Place',
    endpoint: 'db/capability-to-pin',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.capability-to-pin.read',
      create: 'api.capability-to-pin.create',
      update: 'api.capability-to-pin.update',
      delete: 'api.capability-to-pin.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      {
        key: 'capability',
        label: 'Capability',
        minWidth: 300,
        render: safeRefRender('db/capability', 'capability'),
      },
      {
        key: 'pin',
        label: 'Place In The Network',
        minWidth: 260,
        headerNoWrap: true,
        render: safeRefRender('db/pin', 'pin'),
      },
    ],
    fields: [
      {
        key: 'capability',
        label: 'Capability',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/capability',
        optionLabel: 'name',
      },
      {
        key: 'pin',
        label: 'Place In The Network',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/pin',
        optionLabel: 'name',
      },
    ],
  },

  safeCapabilityToSecurityFunction: {
    key: 'safeCapabilityToSecurityFunction',
    label: 'Capability → Security Function',
    endpoint: 'db/capability-to-security-function',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.capability-to-security-function.read',
      create: 'api.capability-to-security-function.create',
      update: 'api.capability-to-security-function.update',
      delete: 'api.capability-to-security-function.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      {
        key: 'capability',
        label: 'Capability',
        minWidth: 300,
        render: safeRefRender('db/capability', 'capability'),
      },
      {
        key: 'security_function',
        label: 'Security Function',
        minWidth: 240,
        headerNoWrap: true,
        render: safeRefRender('db/security-function', 'security_function'),
      },
      { key: 'updated_by', label: 'Updated By', minWidth: 180 },
    ],
    fields: [
      {
        key: 'capability',
        label: 'Capability',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/capability',
        optionLabel: 'name',
      },
      {
        key: 'security_function',
        label: 'Security Function',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/security-function',
        optionLabel: 'name',
      },
    ],
  },

  safeCapabilityToProduct: {
    key: 'safeCapabilityToProduct',
    label: 'Capability → Product',
    endpoint: 'db/capability-to-product',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.capability-to-product.read',
      create: 'api.capability-to-product.create',
      update: 'api.capability-to-product.update',
      delete: 'api.capability-to-product.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      {
        key: 'capability',
        label: 'Capability',
        minWidth: 280,
        render: safeRefRender('db/capability', 'capability'),
      },
      {
        key: 'product',
        label: 'Product',
        minWidth: 280,
        render: safeRefRender('db/product', 'product', 'name', SAFE_PRODUCT_OPTION_PARAMS),
      },
      // `framework_tool` rows are owned by the merge and are rewritten by the next run of it;
      // `manual` rows are safe to edit here.
      { key: 'source', label: 'Source', minWidth: 140 },
      { key: 'framework_tool_id', label: 'Framework Tool ID', minWidth: 150, headerNoWrap: true },
      { key: 'description', label: 'Description', minWidth: 300, truncateAt: 100 },
    ],
    fields: [
      {
        key: 'capability',
        label: 'Capability',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/capability',
        optionLabel: 'name',
      },
      {
        key: 'product',
        label: 'Product',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/product',
        optionsParams: SAFE_PRODUCT_OPTION_PARAMS,
        optionLabel: 'name',
      },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
      { key: 'source', label: 'Source (framework_tool / manual / derived)', type: 'text' },
    ],
  },

  safeFrameworkToolProductAliases: {
    key: 'safeFrameworkToolProductAliases',
    label: 'Framework Tool Aliases',
    endpoint: 'db/framework-tool-product-alias',
    perms: {
      pageView: 'page.safeAdmin.view',
      read: 'api.framework-tool-product-alias.read',
      create: 'api.framework-tool-product-alias.create',
      update: 'api.framework-tool-product-alias.update',
      delete: 'api.framework-tool-product-alias.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 80 },
      { key: 'framework_tool_name', label: 'Framework Tool Name', minWidth: 280, headerNoWrap: true },
      { key: 'framework_tool_id', label: 'Framework Tool ID', minWidth: 150, headerNoWrap: true },
      {
        key: 'product',
        label: 'Toolbox Product',
        minWidth: 280,
        headerNoWrap: true,
        render: safeRefRender('db/product', 'product', 'name', SAFE_PRODUCT_OPTION_PARAMS),
      },
      { key: 'resolution', label: 'Resolution', minWidth: 140 },
      { key: 'notes', label: 'Notes', minWidth: 300, truncateAt: 100 },
    ],
    fields: [
      {
        key: 'framework_tool_name',
        label: 'Framework Tool Name',
        type: 'text',
        required: true,
      },
      { key: 'framework_tool_id', label: 'Framework Tool ID', type: 'text' },
      {
        key: 'product',
        label: 'Toolbox Product',
        type: 'select',
        optionsEndpoint: 'db/product',
        optionsParams: SAFE_PRODUCT_OPTION_PARAMS,
        optionLabel: 'name',
      },
      // `unmatched` is the state to work through; `absent` means "this product genuinely has no
      // toolbox equivalent", which is why `notes` matters on that one.
      {
        key: 'resolution',
        label: 'Resolution (unmatched / exact / manual / absent)',
        type: 'text',
      },
      { key: 'notes', label: 'Notes', type: 'text', multiLine: true },
    ],
  },

  // --- Modular campaigns -----------------------------------------------------
  // Eight generic-CRUD tables behind the `Campaign Admin` role (page.campaignAdmin.view +
  // api.campaign*.<action>). Every write is a full-record PUT, so each model's `fields` carries all
  // of its required columns; the JSON columns (metadata, provider_config, audience_rules) are left
  // out because the backend defaults them, and the self-FK / composed FKs are `select` pickers.
  campaigns: {
    key: 'campaigns',
    label: 'Campaigns',
    endpoint: 'db/campaign',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign.read',
      create: 'api.campaign.create',
      update: 'api.campaign.update',
      delete: 'api.campaign.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'name', label: 'Name', minWidth: 240 },
      { key: 'key', label: 'Key', minWidth: 160 },
      { key: 'status', label: 'Status', minWidth: 110 },
      { key: 'version', label: 'Version', minWidth: 90 },
      { key: 'owner', label: 'Owner', minWidth: 200 },
      { key: 'is_active', label: 'Active', minWidth: 90 },
    ],
    fields: [
      { key: 'key', label: 'Key', type: 'text', required: true },
      { key: 'slug', label: 'Slug', type: 'text', required: true },
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
      { key: 'image_url', label: 'Image URL', type: 'text' },
      { key: 'status', label: 'Status (draft/published/archived)', type: 'text' },
      { key: 'owner', label: 'Owner (email)', type: 'text' },
      { key: 'estimated_duration_minutes', label: 'Est. Duration (min)', type: 'text' },
      { key: 'is_active', label: 'Active', type: 'boolean' },
    ],
  },

  campaignSteps: {
    key: 'campaignSteps',
    label: 'Steps',
    endpoint: 'db/campaign-step',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign-step.read',
      create: 'api.campaign-step.create',
      update: 'api.campaign-step.update',
      delete: 'api.campaign-step.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'name', label: 'Name', minWidth: 220 },
      { key: 'step_kind', label: 'Kind', minWidth: 130 },
      { key: 'track', label: 'Track', minWidth: 120 },
      { key: 'order', label: 'Order', minWidth: 80 },
    ],
    fields: [
      {
        key: 'campaign',
        label: 'Campaign',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/campaign',
        optionLabel: 'name',
      },
      {
        key: 'parent',
        label: 'Parent Step',
        type: 'select',
        optionsEndpoint: 'db/campaign-step',
        optionLabel: 'name',
      },
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'text', multiLine: true },
      { key: 'order', label: 'Order', type: 'text' },
      { key: 'track', label: 'Track (enablement/delivery/followup)', type: 'text' },
      { key: 'step_kind', label: 'Step Kind', type: 'text' },
      {
        key: 'journey_map',
        label: 'Journey Map',
        type: 'select',
        optionsEndpoint: 'db/journey-map',
        optionLabel: 'name',
      },
      {
        key: 'assessment',
        label: 'Assessment',
        type: 'select',
        optionsEndpoint: 'db/assessment',
        optionLabel: 'name',
      },
      { key: 'launch_url', label: 'Launch URL', type: 'text' },
      { key: 'provider', label: 'Provider', type: 'text' },
      { key: 'is_required', label: 'Required', type: 'boolean' },
    ],
  },

  campaignEnrollments: {
    key: 'campaignEnrollments',
    label: 'Enrollments',
    endpoint: 'db/campaign-enrollment',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign-enrollment.read',
      create: 'api.campaign-enrollment.create',
      update: 'api.campaign-enrollment.update',
      delete: 'api.campaign-enrollment.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'campaign', label: 'Campaign', minWidth: 110 },
      { key: 'user_email', label: 'User', minWidth: 220 },
      { key: 'status', label: 'Status', minWidth: 130 },
    ],
    fields: [
      {
        key: 'campaign',
        label: 'Campaign',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/campaign',
        optionLabel: 'name',
      },
      {
        key: 'user',
        label: 'User',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/user',
        optionLabel: 'email',
      },
      { key: 'status', label: 'Status (not_started/in_progress/completed)', type: 'text' },
    ],
  },

  campaignDeliveries: {
    key: 'campaignDeliveries',
    label: 'Deliveries',
    endpoint: 'db/campaign-delivery',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign-delivery.read',
      create: 'api.campaign-delivery.create',
      update: 'api.campaign-delivery.update',
      delete: 'api.campaign-delivery.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'name', label: 'Name', minWidth: 220 },
      { key: 'audience_mode', label: 'Mode', minWidth: 140 },
      { key: 'status', label: 'Status', minWidth: 130 },
      { key: 'owner', label: 'Owner', minWidth: 200 },
    ],
    fields: [
      {
        key: 'campaign',
        label: 'Campaign',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/campaign',
        optionLabel: 'name',
      },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'audience_mode', label: 'Audience Mode (event/single_customer)', type: 'text' },
      { key: 'status', label: 'Status (draft/scheduled/in_progress/completed/cancelled)', type: 'text' },
      { key: 'owner', label: 'Owner (email)', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'text', multiLine: true },
    ],
  },

  campaignDeliveryTargets: {
    key: 'campaignDeliveryTargets',
    label: 'Delivery Targets',
    endpoint: 'db/campaign-delivery-target',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign-delivery-target.read',
      create: 'api.campaign-delivery-target.create',
      update: 'api.campaign-delivery-target.update',
      delete: 'api.campaign-delivery-target.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'delivery', label: 'Delivery', minWidth: 110 },
      { key: 'customer_name', label: 'Customer', minWidth: 220 },
      { key: 'status', label: 'Status', minWidth: 130 },
    ],
    fields: [
      {
        key: 'delivery',
        label: 'Delivery',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/campaign-delivery',
        optionLabel: 'name',
      },
      { key: 'customer_name', label: 'Customer Name', type: 'text' },
      { key: 'status', label: 'Status (not_started/in_progress/completed/cancelled)', type: 'text' },
    ],
  },

  campaignDeliveryStepStatuses: {
    key: 'campaignDeliveryStepStatuses',
    label: 'Step Statuses',
    endpoint: 'db/campaign-delivery-step-status',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign-delivery-step-status.read',
      create: 'api.campaign-delivery-step-status.create',
      update: 'api.campaign-delivery-step-status.update',
      delete: 'api.campaign-delivery-step-status.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'delivery', label: 'Delivery', minWidth: 110 },
      { key: 'step_name', label: 'Step', minWidth: 220 },
      { key: 'status', label: 'Status', minWidth: 130 },
    ],
    fields: [
      {
        key: 'delivery',
        label: 'Delivery',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/campaign-delivery',
        optionLabel: 'name',
      },
      {
        key: 'step',
        label: 'Step',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/campaign-step',
        optionLabel: 'name',
      },
      {
        key: 'target',
        label: 'Target (blank = event-wide)',
        type: 'select',
        optionsEndpoint: 'db/campaign-delivery-target',
        optionLabel: 'customer_name',
      },
      { key: 'status', label: 'Status (not_started/in_progress/completed/skipped)', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'text', multiLine: true },
    ],
  },

  campaignActionItems: {
    key: 'campaignActionItems',
    label: 'Action Items',
    endpoint: 'db/campaign-action-item',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign-action-item.read',
      create: 'api.campaign-action-item.create',
      update: 'api.campaign-action-item.update',
      delete: 'api.campaign-action-item.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'text', label: 'Text', minWidth: 280 },
      { key: 'kind', label: 'Kind', minWidth: 120 },
      { key: 'status', label: 'Status', minWidth: 120 },
      { key: 'order', label: 'Order', minWidth: 80 },
    ],
    fields: [
      {
        key: 'delivery',
        label: 'Delivery',
        type: 'select',
        optionsEndpoint: 'db/campaign-delivery',
        optionLabel: 'name',
      },
      {
        key: 'target',
        label: 'Target',
        type: 'select',
        optionsEndpoint: 'db/campaign-delivery-target',
        optionLabel: 'customer_name',
      },
      {
        key: 'step',
        label: 'Step',
        type: 'select',
        optionsEndpoint: 'db/campaign-step',
        optionLabel: 'name',
      },
      { key: 'kind', label: 'Kind (action_item/followup)', type: 'text' },
      { key: 'text', label: 'Text', type: 'text', required: true, multiLine: true },
      { key: 'owner', label: 'Owner (email)', type: 'text' },
      { key: 'status', label: 'Status (open/in_progress/done/dropped)', type: 'text' },
      { key: 'order', label: 'Order', type: 'text' },
    ],
  },

  campaignReports: {
    key: 'campaignReports',
    label: 'Reports',
    endpoint: 'db/campaign-report',
    perms: {
      pageView: 'page.campaignAdmin.view',
      read: 'api.campaign-report.read',
      create: 'api.campaign-report.create',
      update: 'api.campaign-report.update',
      delete: 'api.campaign-report.delete',
    },
    columns: [
      { key: 'id', label: 'ID', minWidth: 70 },
      { key: 'title', label: 'Title', minWidth: 240 },
      { key: 'campaign', label: 'Campaign', minWidth: 110 },
      { key: 'kind', label: 'Kind', minWidth: 130 },
      { key: 'status', label: 'Status', minWidth: 110 },
    ],
    fields: [
      {
        key: 'campaign',
        label: 'Campaign',
        type: 'select',
        required: true,
        optionsEndpoint: 'db/campaign',
        optionLabel: 'name',
      },
      {
        key: 'enrollment',
        label: 'Enrollment (blank = campaign-wide)',
        type: 'select',
        optionsEndpoint: 'db/campaign-enrollment',
        optionLabel: 'user_email',
      },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'kind', label: 'Kind (summary/fair/assessment)', type: 'text' },
      { key: 'status', label: 'Status (draft/final)', type: 'text' },
    ],
  },
};

/** The `MODELS` keys the Management > SAFE section shows, in tab order. */
export const SAFE_ADMIN_MODEL_KEYS = [
  'safeBusinessTechnologyToRisk',
  'safeRiskToCapability',
  'safeCapabilityToPin',
  'safeCapabilityToSecurityFunction',
  'safeCapabilityToProduct',
  'safeFrameworkToolProductAliases',
  'safeBusinessTechnologies',
  'safeRisks',
  'safeCapabilities',
  'safePins',
  'safeSecurityFunctions',
  'safeDomains',
  'safeImpacts',
] as const;

/** The `MODELS` keys the Management > Campaigns section shows, in tab order. */
export const CAMPAIGN_ADMIN_MODEL_KEYS = [
  'campaigns',
  'campaignSteps',
  'campaignEnrollments',
  'campaignDeliveries',
  'campaignDeliveryTargets',
  'campaignDeliveryStepStatuses',
  'campaignActionItems',
  'campaignReports',
] as const;